import { useEffect, useState } from "react";
import { BsCalendar3 } from "react-icons/bs";
import { LoadingOutlined } from "@ant-design/icons";
import { Col, Row, Tooltip, Typography, message } from "antd";
import api from "../../services/api";
import Button from "../../components/Button";
import { DrawerModal } from "../DrawerModal";
import { DownloadIcon } from "../../pages/Darfs/styles";
import { TabelasBolsa, TabelasExterior } from "./table";
import { useClients } from "../../contexts/ClientsContext";
import { formatCurrency, formatCurrencySmart, monthsExtended, thirdBusinessDayAndMonth } from "../../utils";
import { LoadingContainer, StyledTable, TitleContainer } from "./styles";
import { yearConfigPL4173 } from "../../constants/others/exterior";

interface IProps {
  modalOperationsOpen: boolean;
  setModalOperationsOpen: React.Dispatch<React.SetStateAction<boolean>>;
  clientsSelected: Array<any>;
  handleDarfSend: (
    sendToWhatsapp?: boolean,
    directDownload?: boolean,
    sendEmail?: boolean,
    inputClients?: Array<any>
  ) => Promise<void>;
  handleDarfDownload: (url?: string, idDarf?: string) => Promise<void>;
  loadingDownload?: boolean;
}

const minDarfPrice = 10;

export const OperationModal = ({
  modalOperationsOpen,
  setModalOperationsOpen,
  clientsSelected,
  handleDarfDownload,
  handleDarfSend,
  loadingDownload = false,
}: IProps) => {
  const item = clientsSelected[0];
  const [transaction, setTransaction] = useState<any>();
  const [index, setIndex] = useState(0);
  const { loadingGet, setLoadingGet } = useClients();
  const [showDarfTooltip, setShowDarfToolTip] = useState(false);
  const isExterior = item?.type === 'Exterior'
  const oldModel = (item?.year ?? 2023) < yearConfigPL4173

  function obterNomeMesPassado() {
    const hoje = new Date();
    let mesAnterior = hoje.getMonth() - 1;

    // Tratamento especial para janeiro (mês 0)
    if (mesAnterior < 0) {
      mesAnterior = 11; // Dezembro (mês 11)
    }

    const nomeMesAnterior = new Date(hoje.getFullYear(), mesAnterior, 1)
      .toLocaleString('default', { month: 'long' });

    // Transforma a primeira letra em maiúscula
    const nomeMesAnteriorComMaiuscula = nomeMesAnterior.charAt(0).toUpperCase() + nomeMesAnterior.slice(1);

    return nomeMesAnteriorComMaiuscula;
  }

  useEffect(() => {
    if (item?.username && modalOperationsOpen) {
      setLoadingGet(true);
      api
        .get(`/darf/transaction/${item.username}`, {
          params: {
            month: item?.month,
            year: item?.year,
            type: item?.type,
            payed: item?.darfPago,
            idDarf:
              item?.type?.toLowerCase() === "bolsa"
                ? item?.idDarfBolsa
                : item?.idDarfExterior,
            nAtualizarDarfsPassadas: false,
            nAtualizarIrrf: true,
            atualizouImpostoAcumulado: false,
            nAtualizarPrejuizosAcumulados: true,
            atualizarTotalmente: false,
            atualizouCorretagem: false,
          },
        })
        .then((res) => {
          setTransaction(res.data);
        })
        .catch((err) => {
          message.error(
            "Não foi possível buscar operações. Tente novamente mais tarde"
          );
          setModalOperationsOpen(false);
        })
        .finally(() => {
          setLoadingGet(false);
        });
    } else if (!modalOperationsOpen) {
      setTransaction(undefined);
    }
  }, [setModalOperationsOpen, modalOperationsOpen, item, setLoadingGet]);

  const valorPrincipal =
    item?.type === "Exterior"
      ? Math.max((transaction?.impostoDevido || 0) - (transaction?.irrfExterior ?? 0), 0)
      : Math.max(
        0,
        Number(
          (transaction?.impostoComumFinal || 0) +
          (transaction?.impostoDTFinal || 0) +
          (transaction?.impostoFIIFinal || 0)
        ) +
        (Number(transaction?.["impostoAcumulado"] || 0) -
          Number(transaction?.["irrfTotal"] || 0))
      );

  const impostoTotal =
    item?.type === "Exterior"
      ? ((valorPrincipal - transaction?.irrfExterior) > 0 ?
        valorPrincipal - (transaction?.irrfExterior || 0) : 0) +
      (transaction?.impostoDevido + transaction?.impostoAcumulado < minDarfPrice
        ? 0
        : transaction?.multa + transaction?.juros)
      : item?.impostoDevido || 0; // Usar o mesmo valor da lista para Bolsa

  const impostoDevido =
    item?.type === "Exterior"
      ? transaction?.impostoDevido || 0
      : Number(
        (transaction?.impostoComumFinal || 0) +
        (transaction?.impostoDTFinal || 0) +
        (transaction?.impostoFIIFinal || 0)
      );


  return (
    <DrawerModal
      width={700}
      open={modalOperationsOpen}
      className="operationsModal"
      onCancel={() => setModalOperationsOpen(false)}
      title={
        <TitleContainer>
          <BsCalendar3 />
          {`${monthsExtended[(clientsSelected[0]?.month || 1) - 1]} de ${clientsSelected[0]?.year
            }`}
        </TitleContainer>
      }
      footer={
        impostoTotal > 0 ? (
          <Row>
            <Col span={24}>
              {
                item?.type === "Bolsa" && thirdBusinessDayAndMonth(clientsSelected[0]).disabled
                  ?
                  <Tooltip
                    title={thirdBusinessDayAndMonth(clientsSelected[0]).title}
                    open={showDarfTooltip}
                  >
                    {((clientsSelected[0]?.year <= 2023 && isExterior) || !isExterior) && <>
                      <Button
                        block
                        tooltip
                        size="large"
                        type="primary"
                        icon={<DownloadIcon className="send-icon" />}
                        onClick={() => {
                          setShowDarfToolTip(!showDarfTooltip)

                          setTimeout(() => {
                            setShowDarfToolTip(false)
                          }, 1500);
                        }}
                      >
                        {isExterior && !oldModel ? 'Enviar relatório' : 'Baixar DARF'}
                      </Button>
                    </>}
                  </Tooltip>
                  :
                  <>{((clientsSelected[0]?.year <= 2023 && isExterior) || !isExterior) && <Button
                    block
                    size="large"
                    type="primary"
                    icon={<DownloadIcon className="send-icon" style={{stroke: "white", fill: "white", color: "white"}}/>}
                    onClick={() => {
                      if (
                        (item.urlDarfExterior ||
                          item.urlDarfBolsa ||
                          item.idDarfBolsa ||
                          item.idDarfExterior) &&
                        item?.darfEnviado
                      ) {
                        handleDarfDownload(
                          item.type === "Bolsa"
                            ? item.urlDarfBolsa
                            : item.urlDarfExterior,
                          item.type === "Bolsa"
                            ? item.idDarfBolsa
                            : item.idDarfExterior
                        );
                      } else {
                        handleDarfSend(false, true, false, [item]);
                      }
                    }}
                    loading={loadingDownload}
                    disabled={
                      !clientsSelected || clientsSelected.length === 0 || loadingGet || loadingDownload || thirdBusinessDayAndMonth(clientsSelected[0]).disabled
                    }
                  >
                    {isExterior && !oldModel ? 'Enviar relatório' : 'Baixar DARF'}
                  </Button>}</>
              }
            </Col>
          </Row>
        ) : null
      }
      bodyStyle={{ position: "relative" }}
    >
      {item?.type === "Bolsa" ? (
        <TabelasBolsa
          transaction={transaction}
          index={index}
          setIndex={setIndex}
        />
      ) : (
        <TabelasExterior transaction={transaction} oldModel={oldModel} year={clientsSelected[0]?.year} />
      )}

      {((clientsSelected[0]?.year <= 2023 && isExterior) || !isExterior) && <Row style={{ marginTop: "2rem" }}>
        <Col style={{ flexGrow: "1" }}>
          <Typography.Title level={5} style={{ fontSize: "20px" }}>
            Imposto Total
          </Typography.Title>
        </Col>
        <Col
          span={10}
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <Typography.Title level={5} style={{ fontSize: "20px" }}>
            {formatCurrencySmart(Math.max(impostoTotal, 0))}
          </Typography.Title>
        </Col>
      </Row> }

      {((clientsSelected[0]?.year <= 2023 && isExterior) || !isExterior) && <StyledTable>
        <Row className="operationsRow resumoRow">
          <Col className="rowLabel">(+) Valor do principal</Col>
          <Col span={10}>{(transaction?.impostoDevido + transaction?.impostoAcumulado - transaction?.irrfExterior) > 0
            ? formatCurrency(valorPrincipal - (transaction?.irrfExterior || 0))
            : formatCurrency(0)}</Col>
        </Row>
        <Row className="operationsRow resumoRow">
          <Col className="rowLabel secondary">(+) Imposto devido</Col>
          <Col span={10}>{formatCurrencySmart(impostoDevido)}</Col>
        </Row>
        {(!isExterior || oldModel) && <Row className="operationsRow resumoRow">
          <Col className="rowLabel secondary">
            (+) DARFs passados abaixo de R$ 10
          </Col>
          <Col span={10}>{formatCurrency(transaction?.impostoAcumulado)}</Col>
        </Row>}
        {(!isExterior || !oldModel) && (
          <Row className="operationsRow resumoRow">
            <Col className="rowLabel secondary">
              (-) Imposto de Renda Retido na Fonte
            </Col>
            <Col span={10}>{formatCurrency(transaction?.irrfTotal || transaction?.irrfExterior)}</Col>
          </Row>
        )}
        {(!isExterior || oldModel) &&
          <>
            <Row className="operationsRow resumoRow">
              <Col className="rowLabel">(+) Multa de atraso</Col>
              <Col span={10}>
                {formatCurrency(valorPrincipal < minDarfPrice ? 0 : transaction?.multaAux || transaction?.multa)}
              </Col>
            </Row>
            <Row className="operationsRow resumoRow">
              <Col className="rowLabel">(+) Juros e/ou encargos de atraso</Col>
              <Col span={10}>
                {formatCurrency(valorPrincipal < minDarfPrice ? 0 : transaction?.jurosAux || transaction?.juros)}
              </Col>
            </Row>
          </>
        }
      </StyledTable>}

      {loadingGet && (
        <LoadingContainer>
          <LoadingOutlined />
        </LoadingContainer>
      )}
    </DrawerModal>
  );
};
